export function debounce<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>;

    return function executedFunction(...args: Parameters<T>): void {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Debounces an async (or sync) function, resolving with the result of the
 * most recent call once `wait` ms have passed without another invocation.
 * Useful for gating requests (e.g. Algolia queries) behind a typing pause.
 */
export function debouncePromise<TArgs extends unknown[], TResult>(
    func: (...args: TArgs) => TResult,
    wait: number
): (...args: TArgs) => Promise<Awaited<TResult>> {
    let timeout: ReturnType<typeof setTimeout>;

    return function debounced(...args: TArgs): Promise<Awaited<TResult>> {
        clearTimeout(timeout);
        return new Promise((resolve) => {
            timeout = setTimeout(() => resolve(func(...args) as Awaited<TResult>), wait);
        });
    };
}
